import React, { memo, useCallback } from 'react'
import { generatePath, Link } from 'react-router-dom'

import { ListItemIcon, Menu, MenuItem } from '@mui/material'
import {
  CloudUploadOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  MoreVertOutlined,
  SaveAsOutlined,
} from '@mui/icons-material'

import Routes from '@tabeeb/routes'
import { IconButton, useDialogState, usePopoverState } from '@collaborate-health/uikit'
import { AnnouncementBaseResponse } from '@tabeeb/contracts/microservices/swagger-article-service'

import AnnouncementDeleteConfirmationDialog from '../AnnouncementDeleteConfirmationDialog'

import { ArticlePublicationStatus } from '../../enums'

export interface AnnouncementMenuProps {
  announcement: AnnouncementBaseResponse
  onDelete: (params: { id: number }) => void
  onUpdateStatus: (params: { id: number; status: ArticlePublicationStatus }) => void
}

const AnnouncementMenu: React.FC<AnnouncementMenuProps> = ({ announcement, onDelete, onUpdateStatus }) => {
  const [menuAnchorRef, isMenuOpen, onOpenMenu, onCloseMenu] = usePopoverState<HTMLButtonElement>()
  const [isDeleteAnnouncementDialogOpen, onOpenDeleteAnnouncementDialog, onCloseDeleteAnnouncementDialog] =
    useDialogState()

  const onDeleteAnnouncementSubmit = useCallback(() => {
    onDelete({ id: announcement.Id })
  }, [announcement.Id, onDelete])

  return (
    <>
      <IconButton ref={menuAnchorRef} onClick={onOpenMenu}>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={isMenuOpen}
        onClose={onCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Link to={generatePath(Routes.announcementEdit, { id: announcement.Id })}>
          <MenuItem
            title='Edit'
            onClick={() => {
              onCloseMenu()
            }}
          >
            <ListItemIcon>
              <EditOutlined />
            </ListItemIcon>
            Edit
          </MenuItem>
        </Link>
        {announcement.Status !== ArticlePublicationStatus.Published && (
          <MenuItem
            title='Publish'
            onClick={() => {
              onCloseMenu()
              onUpdateStatus({ id: announcement.Id, status: ArticlePublicationStatus.Published })
            }}
          >
            <ListItemIcon>
              <CloudUploadOutlined />
            </ListItemIcon>
            Publish
          </MenuItem>
        )}
        {announcement.Status !== ArticlePublicationStatus.Draft && (
          <MenuItem
            title='Mark as draft'
            onClick={() => {
              onCloseMenu()
              onUpdateStatus({ id: announcement.Id, status: ArticlePublicationStatus.Draft })
            }}
          >
            <ListItemIcon>
              <SaveAsOutlined />
            </ListItemIcon>
            Mark as draft
          </MenuItem>
        )}
        <MenuItem
          title='Delete'
          onClick={() => {
            onCloseMenu()
            onOpenDeleteAnnouncementDialog()
          }}
        >
          <ListItemIcon>
            <DeleteOutlineOutlined color='error' />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <AnnouncementDeleteConfirmationDialog
        open={isDeleteAnnouncementDialogOpen}
        onClose={onCloseDeleteAnnouncementDialog}
        onSubmit={onDeleteAnnouncementSubmit}
      />
    </>
  )
}

export default memo(AnnouncementMenu)

